import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, switchMap } from 'rxjs';

import { Chat, ChatCmsSchedule, ChatDaySchedule, ChatSchedule, ChatWeekDays } from '../models/chat.models';
import { EnableUrlChat } from '../store/chat.actions';

import { ApiChatService } from './api-chat.service';

import { getCmsConfigValue, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { CoreDatesService } from '@core/services/date/dates.service';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { UiService } from '@core/services/ui.service';
import { DuttiDate, DuttiDateFormat } from '@frontend/common/duttidate';

@Injectable()
export class ChatService {
    constructor(
        private readonly uiService: UiService,
        private readonly store: Store,
        private readonly storageService: CoreStorageService,
        private readonly apiChatService: ApiChatService,
        private readonly dateService: CoreDatesService,
    ) {}

    private readonly emtyInfo = { isOutOfService: false, weeklySchedule: [] };

    public isWebapp(): boolean {
        return this.uiService.ui.webapp;
    }

    public openHubtype(): void {
        if (Botonic) {
            Botonic.open();

            // Añadimos en local storge un control
            // para saber que estamos con una conversación abierta (Solo Desktop)
            if (!this.uiService.ui.webapp) {
                this.storageService.setValue('hubtypeInUse', true, 'PERMANENT', 'TECHNICAL');
            }
        }
    }

    public openUrlChat(chat: Chat): void {
        this.store.dispatch(EnableUrlChat({ payload: chat.configuration }));
    }

    public scheduleInfo(cms: boolean) {
        return combineLatest([
            cms ? this.cmsInfo() : this.mocaInfo(),
            this.dateService.getCurrentDate(),
        ]).pipe(
            map(([schedule, date]) => ({
                valid: ChatService.chatValid(schedule),
                active: ChatService.chatActive(schedule, date),
                scheduleMessage: ChatService.chatScheduleMessage(schedule),
            }))
        );
    }

    private cmsInfo(): Observable<ChatCmsSchedule> {
        return this.store.select(getCmsConfigValue('chat_schedule')).pipe(
            map((data) => data || this.emtyInfo),
            map(data => ({ cms: true, ...data })),
        );
    }

    private mocaInfo(): Observable<ChatSchedule> {
        return this.store.select(getStoreAndLanguage).pipe(
            switchMap(({ storeId, languageId }) => this.apiChatService.workgroup(storeId, languageId)),
            map((data) => data || this.emtyInfo),
            map(data => ({ cms: false, ...data })),
        );
    }

    private static chatScheduleMessage(schedule: ChatCmsSchedule | ChatSchedule): string {
        if (schedule.isOutOfService) {
            if (schedule.cms) {
                return 'dev.contact.chatnotavailable';
            }
            return schedule.outOfServiceMessage;
        }
        if (schedule.cms) {
            return 'dev.contact.chat.schedule';
        }
        return schedule.outOfScheduleMessage;
    }

    private static chatValid(schedule: ChatCmsSchedule | ChatSchedule): boolean {
        if (schedule.cms) {
            return schedule.weeklySchedule?.length > 0;
        }
        // Es válido si tiene al menos un día con horario en openHours
        return schedule.weeklySchedule?.length > 0 && ChatService.chatHasOpenHours(schedule as ChatSchedule);
    }

    private static chatHasOpenHours(schedule: ChatSchedule): boolean {
        return schedule.weeklySchedule.some((day) => day.openHours?.length > 0);
    }

    private static chatActive(schedule: ChatCmsSchedule | ChatSchedule, currentDate: DuttiDate): boolean {
        if (schedule.isOutOfService) {
            return false;
        }

        const daySchedules: ChatDaySchedule[] = schedule.cms
            ? ChatService.getCmsDaySchedules(schedule, currentDate) || []
            : (schedule as ChatSchedule).weeklySchedule?.[0]?.openHours || [];

        for (let daySchedule of daySchedules) {
            const isActive = ChatService.isBetween(currentDate, daySchedule.fromTime, daySchedule.toTime);
            if (isActive) {
                return true;
            }
        }
        return false;
    }

    private static getCmsDaySchedules(schedule: ChatCmsSchedule, currentDate: DuttiDate): ChatDaySchedule[] {
        const days: Record<ChatWeekDays, number> = {
            monday: 1,
            tuesday: 2,
            wednesday: 3,
            thursday: 4,
            friday: 5,
            saturday: 6,
            sunday: 7,
        };
        if (schedule.weeklySchedule) {
            for (let weekDay of schedule.weeklySchedule) {
                const day: ChatWeekDays = Object.keys(weekDay)[0] as ChatWeekDays;
                if (currentDate.isoWeekday() === days[day]) {
                    return weekDay[day];
                }
            }
        }
    }

    private static isBetween(current: DuttiDate, from: string, to: string): boolean {
        const date = current.format(DuttiDateFormat.DATE);
        const localDate = new DuttiDate( // .local() falla...
            current.format(DuttiDateFormat.ISO_NO_UTC),
            DuttiDateFormat.ISO_NO_UTC
        );
        // Utilizamos el día "current" para hacer comparaciones
        return localDate.isBetween(
            new DuttiDate(`${date} ${from}`, DuttiDateFormat.DATE_CHAT),
            new DuttiDate(`${date} ${to}`, DuttiDateFormat.DATE_CHAT)
        );
    }
}
